import type { PipeTransform } from '@angular/core';
import { Pipe } from '@angular/core';
import { DateTime } from 'luxon';
import type { OnyxOptional } from '../interfaces';

export enum OnyxDateFormat {
  DATE = 'date',
  DATE_DOT = 'date-dot',
  DATE_TIME = 'date-time',
  DATE_DOT_TIME = 'date-dot-time',
  TIME_DATE = 'time-date',
  TIME_DATE_DOT = 'time-date-dot',
  TIME_DAY_DATE = 'time-day-date',
  TIME_DOT_DATE = 'time-dot-date',
  DAY_DATE = 'day-date',
  WEEK_DAY = 'week-day',
  ABBREVIATED_WEEK_DAY = 'abbreviated-week-day',
}

export interface OnyxDateFormatOptions {
  endDate?: OnyxOptional<string | DateTime>;
  showSameYear?: boolean;
  styled?: boolean;
  separator?: string;
  showSeconds?: boolean;
  showGap?: boolean;
}

@Pipe({
  name: 'onyxDate',
})
export class OnyxDatePipe implements PipeTransform {
  public transform(
    startDate: OnyxOptional<string | DateTime>,
    format: `${OnyxDateFormat}`,
    options?: OnyxDateFormatOptions,
  ): string {
    if (!startDate) return '-';

    const {
      endDate,
      showSameYear = false,
      styled,
      separator = ', ',
      showSeconds,
      showGap = true,
    } = options ?? {};

    const startDateTime = DateTime.isDateTime(startDate)
      ? startDate
      : DateTime.fromJSDate(new Date(startDate as string));

    const endDateTime = endDate
      ? DateTime.isDateTime(endDate)
        ? endDate
        : DateTime.fromJSDate(new Date(endDate as string))
      : startDateTime;

    const dates = this.transformDatesRange(
      startDateTime,
      endDateTime,
      format,
      showSameYear,
      showGap,
    );
    const hours = this.transformHoursRange(
      startDateTime,
      endDateTime,
      showSeconds,
      showGap,
    );

    const style = (label: string): string => {
      if (!styled) return label;
      return `<span class="f-muted">${label.replaceAll(`${separator}`, ' · ')}</span>`;
    };

    return {
      [OnyxDateFormat.DATE]: dates,
      [OnyxDateFormat.DATE_DOT]: dates,
      [OnyxDateFormat.DATE_TIME]: `${dates}${style(`${separator}${hours}`)}`,
      [OnyxDateFormat.DATE_DOT_TIME]: `${dates}${style(`${separator}${hours}`)}`,
      [OnyxDateFormat.TIME_DATE]: `${hours}${style(`${separator}${dates}`)}`,
      [OnyxDateFormat.TIME_DATE_DOT]: `${hours}${style(`${separator}${dates}`)}`,
      [OnyxDateFormat.TIME_DAY_DATE]: `${hours}${style(`${separator}${dates}`)}`,
      [OnyxDateFormat.TIME_DOT_DATE]: `${hours}${style(`${separator}${dates}`)}`,
      [OnyxDateFormat.DAY_DATE]: dates,
      [OnyxDateFormat.WEEK_DAY]: dates,
      [OnyxDateFormat.ABBREVIATED_WEEK_DAY]: dates,
    }[format];
  }

  private transformDatesRange(
    startDate: DateTime,
    endDate: DateTime,
    format: `${OnyxDateFormat}`,
    showSameYear: boolean,
    showGap = true,
  ): string {
    const separator = showGap ? ' - ' : '-';
    const formatDateTime = (dateTime: DateTime, hideYear = false): string => {
      const FORMATS: Record<OnyxDateFormat, string> = {
        [OnyxDateFormat.DATE]: 'd LLL',
        [OnyxDateFormat.DATE_DOT]: 'dd.MM',
        [OnyxDateFormat.DATE_TIME]: 'd LLL',
        [OnyxDateFormat.DATE_DOT_TIME]: 'dd.MM',
        [OnyxDateFormat.TIME_DATE]: 'd LLL',
        [OnyxDateFormat.TIME_DATE_DOT]: 'dd.MM',
        [OnyxDateFormat.TIME_DAY_DATE]: 'ccc d LLL',
        [OnyxDateFormat.TIME_DOT_DATE]: 'dd.MM',
        [OnyxDateFormat.DAY_DATE]: 'ccc d LLL',
        [OnyxDateFormat.WEEK_DAY]: 'cccc',
        [OnyxDateFormat.ABBREVIATED_WEEK_DAY]: 'ccc',
      };

      const year = !hideYear
        ? format.includes('dot')
          ? '.yyyy'
          : ' yyyy'
        : '';

      return dateTime.toFormat(`${FORMATS[format]}${year}`);
    };

    startDate = startDate.startOf('day');
    endDate = endDate.startOf('day');

    const sameYear =
      startDate.hasSame(endDate, 'year') &&
      startDate.hasSame(DateTime.now(), 'year');
    const end = formatDateTime(endDate, sameYear && !showSameYear);
    if (endDate.equals(startDate)) return end;

    if (endDate.hasSame(startDate, 'month')) {
      const start = startDate.toFormat(format.includes('dot') ? 'dd' : 'd');
      return `${start}${separator}${end}`;
    }

    if (sameYear) {
      const start = formatDateTime(startDate, true);
      return `${start}${separator}${end}`;
    }

    const start = formatDateTime(startDate);
    return `${start}${separator}${end}`;
  }

  private transformHoursRange(
    startDate: DateTime,
    endDate: DateTime,
    showSeconds = false,
    showGap = true,
  ): string {
    const format = showSeconds ? 'HH:mm:ss' : 'HH:mm';

    const start = startDate.toFormat(format);
    const end = endDate.toFormat(format);

    if (start === end) return start;
    return `${start}${showGap ? ' - ' : '-'}${end}`;
  }
}
